import Box from "@/common/components/Box";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { ShowHeader } from "@/common/components/ShowHeader";
import { Building2Icon, CalendarDaysIcon } from "lucide-react";
import NegotiationHelper from "../../components/NegotiationHelper";
import EquipmentCategorySummary from "../../components/EquipmentCategorySummary";
import NegotiatingProductsShow from "../../components/Show/NegotiatingProducts";
import ResumeBox from "@/common/components/ResumeBox";
import CancelNegotiationModal from "../../components/Show/CancelNegotiationModal";
import AcceptNegotiationModal from "../../components/Show/AcceptNegotiationModal";
import useNegotiation from "../../hooks/useNegotiation";
import { useParams } from "react-router-dom";
import { negotiationStatus } from "../../constants/negotiationStatus.constant";
import NegotiationsShowLoadingState from "./LoadingState";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { negotiationCounterSchema, NegotiationCounterValidationSchema } from "../../schemas/negotiationCounter.schema";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import useNegotiationSendToEstablishment from "../../hooks/useNegotiationSendToEstablishment";
import { negotiationCounterDefaultValues } from "../../services/negotiationCounterDefaultValues";
import { useEffect, useState } from "react";
import Button from "@/common/components/Button";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { formatDate } from "@/common/utils/formatters";

const NegotiationsShow = () => {
  const { id } = useParams();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: negotiation,
    isPending: negotiationIsPending,
    isFetched: negotiationIsFetched,
    refetch: negotiationRefetch,
    error: negotiationError = {} as any,
  } = useNegotiation(id);

  const {
    mutate: sendToEstablishment,
    isPending: sendToEstablishmentIsPending,
    isError: sendToEstablishmentFailed,
  } = useNegotiationSendToEstablishment({
    negotiationId: id,
    onSuccess: (response) => {
      setMessages(['Contraproposta enviada ao estabelecimento']);
      queryClient.invalidateQueries({ queryKey: ['negotiations', id] });

      reset(negotiationCounterDefaultValues(response.data.data));
    },
    onError: (error) => {
      setMessages(error.response?.data.errors?.base || ['Ocorreu um erro ao enviar contraproposta']);
    }
  });

  const formMethods = useForm<NegotiationCounterValidationSchema>({
    resolver: zodResolver(negotiationCounterSchema),
    disabled: negotiation?.status && (['accepted', 'canceled', 'expired', 'awaiting_establishment_response', 'rejected'] as (keyof typeof negotiationStatus)[]).includes(negotiation?.status),
    defaultValues: negotiationCounterDefaultValues(negotiation),
  });

  const { reset, watch, getValues, formState: { disabled } } = formMethods;

  const onSubmit = () => sendToEstablishment(getValues());

  const watchNegotiationProducts = watch('negotiation_products_attributes');

  const hasSomeCounter = watchNegotiationProducts.some((negotiationProduct) => negotiationProduct.desired_action === 'counter');

  useEffect(() => {
    if (negotiationIsFetched) {
      reset(negotiationCounterDefaultValues(negotiation));
    }
  }, [negotiationIsFetched]);

  if (negotiationIsPending) {
    return <NegotiationsShowLoadingState />;
  }

  return (
    <FormProvider {...formMethods}>
      <div className="flex">
        <ShowHeader
          goBack
          goBackProps={{ fallback: '/negotiations', searchLastRouteFromReference: `/negotiations/${id}` }}
          titleClassName="text-nowrap font-medium text-lg"
          title="Negociações"
          subtitle={negotiation?.number}
          className="w-full"
          buttons={(
            <>
              <div className="flex items-center gap-2">
                <CancelNegotiationModal negotiationStatus={negotiation?.status} />

                {!disabled && !hasSomeCounter && (
                  <AcceptNegotiationModal
                    onConfirm={onSubmit}
                    isLoading={sendToEstablishmentIsPending}
                  />
                )}

                {!disabled && hasSomeCounter && (
                  <Button
                    onClick={onSubmit}
                    isLoading={sendToEstablishmentIsPending}
                  >
                    Enviar contraproposta
                  </Button>
                )}
              </div>
            </>
          )}
        />
      </div>

      <ErrorMessageWrapper
        refetchFunction={negotiationRefetch}
        statusCode={negotiationError?.status}
      >
        <Box className="flex gap-6 h-[calc(100vh-128px)] overflow-y-auto">
          <div className="w-1/2 flex flex-col gap-4 h-max">
            <div className="flex items-center gap-2">
              <ImageWithPlaceholder
                placeholderIcon={Building2Icon}
                url={negotiation?.establishment_company?.general_information?.company_logo?.url}
                className="size-6"
                iconClassName="size-4"
              />

              <p className="text-lg font-bold">
                {negotiation?.establishment_company?.general_information?.fantasy_name}
              </p>
            </div>

            <ResumeBox
              label="Status"
              value={negotiation?.status ? negotiationStatus[negotiation?.status] : ''}
              labelClassName="text-md font-bold"
              valueClassName="font-light"
              boxClassName={
                negotiation?.status === 'awaiting_client_response' ? 'bg-yellow-op-2' :
                  negotiation?.status === 'accepted' ? 'bg-success-op' :
                    ['canceled', 'rejected'].includes(negotiation?.status || '') ? 'bg-danger-op' :
                      negotiation?.status === 'awaiting_establishment_response' ? 'bg-slate-400/30' :
                        ''
              }
            />

            <NegotiationHelper />

            <div className="flex flex-col gap-1">
              <p className="text-xs">Proposta da empresa</p>

              <Box className="p-4 text-sm bg-white-200 rounded-md max-h-[280px] overflow-y-auto">
                {negotiation?.observation}
              </Box>
            </div>

            {['accepted', 'expired'].includes(negotiation?.status || '') && negotiation?.due_at && (
              <ResumeBox
                label="Validade da negociação"
                value={(
                  <div className="flex items-center gap-1">
                    <CalendarDaysIcon strokeWidth={1.5} size={20} />

                    <p>{formatDate(new Date(negotiation?.due_at))}</p>
                  </div>
                )}
              />
            )}

            <EquipmentCategorySummary />
          </div>

          <div className="w-1/2 flex flex-col gap-4 h-max">
            <NegotiatingProductsShow
              negotiationStatus={negotiation?.status}
              negotiationProducts={negotiation?.negotiation_products}
              establishment={negotiation?.establishment_company}
            />
          </div>
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError={sendToEstablishmentFailed}
          />
        )}
      </ErrorMessageWrapper>
    </FormProvider>
  );
};

export default NegotiationsShow;
